import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import guideCover from "@/assets/guide/guide-cover.jpg";

const PDF_PATH = "/guide/salonturbo-3-tage-regel.pdf";

export const Route = createFileRoute("/guide")({
  head: () => ({
    meta: [
      { title: "Gratis-Guide: Die 3-Tage-Regel für Friseursalons" },
      {
        name: "description",
        content:
          "Kostenloses 16-seitiges PDF für Friseursalons: So füllst du kurzfristige Terminausfälle, ohne Rabatte zu geben. Direkt herunterladen, ohne Anmeldung.",
      },
      { property: "og:title", content: "Gratis-Guide: Die 3-Tage-Regel für Friseursalons" },
      {
        property: "og:description",
        content:
          "16 Seiten Praxiswissen: kurzfristige Lücken im Kalender füllen, ohne die eigenen Preise zu verbrennen.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: GuidePage,
});

const contents = [
  "Warum Rabatte kurzfristig helfen und langfristig schaden",
  "Die 3-Tage-Regel: der einfache Rhythmus für deinen Kalender",
  "Welche Kunden spontan suchen – und wie du sie erreichst",
  "Formulierungen, die statt Preisnachlass Wertigkeit zeigen",
  "Checklisten für Montag, Mitte der Woche und Wochenende",
  "Der Kurzweg: Tage boosten statt jede Woche selbst werben",
];

function GuidePage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="mx-auto max-w-6xl px-4 pb-16 pt-12 sm:pt-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="eyebrow text-primary">Kostenloser Guide · 16 Seiten PDF</p>
              <h1 className="mt-4 font-display text-3xl font-light leading-[1.1] sm:text-5xl">
                Die 3-Tage-Regel: Wie du kurzfristige Ausfälle ohne Rabatte füllst
              </h1>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Ein praktischer Leitfaden für Salon-Inhaber und Stuhlmieter: klare Routinen, konkrete
                Formulierungen und ein Weg, kurzfristige Lücken zu füllen, ohne die eigenen Preise
                abzuwerten.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button asChild size="lg">
                  <a href={PDF_PATH} download>
                    <Download className="mr-1 size-4" /> Guide herunterladen (PDF)
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/auth">
                    Jetzt starten <ArrowRight className="ml-1 size-4" />
                  </Link>
                </Button>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Ohne Anmeldung, ohne Newsletter – direkter Download.
              </p>
            </div>
            <div className="order-first lg:order-last">
              <div className="overflow-hidden rounded-3xl border shadow-soft-lg">
                <img
                  src={guideCover}
                  alt="Titelseite des Guides „Die 3-Tage-Regel“"
                  width={1240}
                  height={1754}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">Das steckt drin</h2>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {contents.map((item) => (
                <li key={item} className="flex gap-3 rounded-2xl border bg-card p-5">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" />
                  <span className="text-sm leading-relaxed text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button asChild>
                <a href={PDF_PATH} download>
                  <Download className="mr-1 size-4" /> PDF öffnen
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
